import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Filter, RefreshCw, BarChart3, AlertTriangle, Info, Bug } from 'lucide-react';
import { adminAPI } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

interface Log {
  id: number;
  level: string;
  message: string;
  context?: any;
  timestamp: string;
  userId?: number;
  userEmail?: string;
  userName?: string;
  ipAddress?: string;
  userAgent?: string;
}

interface LogStatistics {
  statistics: Array<{
    level: string;
    count: number;
    date: string;
    uniqueUsers: number;
    uniqueIps: number;
  }>;
  topErrors: Array<{
    message: string;
    count: number;
    lastOccurrence: string;
  }>;
  recentActivity: Array<{
    level: string;
    count: number;
    hour: number;
  }>;
  summary: {
    totalLogs: number;
    errorCount: number;
    warningCount: number;
    infoCount: number;
  };
}

interface LogsResponse {
  logs: Log[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalLogs: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

const Logs: React.FC = () => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState('all');
  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<LogsResponse['pagination'] | null>(null);
  const [statistics, setStatistics] = useState<LogStatistics | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSystemLogs(level, limit, page);
      setLogs(response.logs);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  }, [level, limit, page]);

  const loadStatistics = useCallback(async () => {
    try {
      const response = await adminAPI.getLogStatistics();
      setStatistics(response);
    } catch (error) {
      console.error('Error loading log statistics:', error);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (showStatistics) {
      loadStatistics();
    }
  }, [showStatistics, loadStatistics]);

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return 'text-red-600 bg-red-100';
      case 'warn':
        return 'text-yellow-600 bg-yellow-100';
      case 'info':
        return 'text-blue-600 bg-blue-100';
      case 'debug':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Logs système
          </h2>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={loadLogs}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Filter className="h-5 w-5 text-gray-400 mr-2" />
            <label className="text-sm font-medium text-gray-700">Niveau :</label>
          </div>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <option value="all">Tous</option>
            <option value="error">Erreur</option>
            <option value="warn">Avertissement</option>
            <option value="info">Information</option>
            <option value="debug">Debug</option>
          </select>

          <div className="flex items-center">
            <label className="text-sm font-medium text-gray-700 mr-2">Limite :</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun log</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aucun log trouvé pour les critères sélectionnés.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {logs.map((log) => (
              <li key={log.id} className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLevelColor(log.level)}`}
                      >
                        {log.level.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatTimestamp(log.timestamp)}
                      </span>
                      {log.userId && (
                        <span className="text-xs text-gray-400">
                          User ID: {log.userId}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-gray-900">{log.message}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Activity className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Information sur les logs
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Les logs système sont collectés automatiquement et incluent :
              </p>
              <ul className="mt-1 list-disc list-inside">
                <li>Connexions et déconnexions des administrateurs</li>
                <li>Actions sur les utilisateurs (création, modification, suppression)</li>
                <li>Erreurs et avertissements du système</li>
                <li>Activité générale de l'application</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Logs;
